import { useCallback, useMemo, useState } from 'react';
import {
  ApolloError,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import { v4 as uuid } from 'uuid';

import {
  AI_CHAT_THREAD,
  AI_CHAT_THREAD_CREATE,
  AI_CHAT_THREAD_DELETE,
  AI_CHAT_THREAD_UPDATE,
  AI_CHAT_THREADS,
} from '@atom/graph/ai';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import {
  AIChatThread,
  AIChatThreadConnection,
  AIChatThreadConnectionInput,
  AIChatThreadCreateInput,
  AIChatThreadUpdateInput,
} from '@atom/types/ai';

export type UseThreadsResponse = {
  currentThread: AIChatThread | null;
  threads: AIChatThread[];
  loading: boolean;
  refreshingThread: boolean;
  error?: ApolloError | null;
  hasMoreThreads: boolean;
  fetchMoreThreads: () => void;
  refreshThread: (threadId: string) => void;
  updateThread: (threadId: string, title: string) => Promise<AIChatThread>;
  createThread: (
    threadId: string,
    optimistic?: boolean,
  ) => Promise<AIChatThread>;
  deleteThread: (threadId: string) => Promise<boolean>;
  startNewThread: () => void;
};

export const THREADS_PER_PAGE = 25;

export const useThreads = (currentThreadId: string) => {
  const [, { updateSearchParams }] = useNavigation({
    threadId: SearchParamType.STRING,
    isNewThread: SearchParamType.BOOLEAN,
  });
  const [threads, setThreads] = useState<AIChatThread[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const currentThread = useMemo(() => {
    return threads.find(({ id }) => id === currentThreadId) || null;
  }, [threads, currentThreadId]);

  const input = useMemo<AIChatThreadConnectionInput>(
    () => ({
      page,
      limit: THREADS_PER_PAGE,
    }),
    [page],
  );

  const { loading, error } = useQuery<
    { aiChatThreads: AIChatThreadConnection },
    { input: AIChatThreadConnectionInput }
  >(AI_CHAT_THREADS, {
    fetchPolicy: 'no-cache',
    variables: { input },
    onCompleted: data => {
      setThreads(current => [...current, ...data.aiChatThreads.threads]);
      setTotal(data.aiChatThreads.totalCount);
    },
  });

  const [getThreadQuery, { loading: refreshingThread }] = useLazyQuery<
    { aiChatThread: AIChatThread },
    { id: string }
  >(AI_CHAT_THREAD, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const updated = data.aiChatThread;
      setThreads(current =>
        current.map(thread => (thread.id === updated.id ? updated : thread)),
      );
    },
  });

  const [createThreadMutation] = useMutation<
    { aiChatThreadCreate: AIChatThread },
    { input: AIChatThreadCreateInput }
  >(AI_CHAT_THREAD_CREATE);

  const [updateThreadMutation] = useMutation<
    { aiChatThreadUpdate: AIChatThread },
    { input: AIChatThreadUpdateInput }
  >(AI_CHAT_THREAD_UPDATE);

  const [deleteThreadMutation] = useMutation<
    { aiChatThreadDelete: boolean },
    { threadId: string }
  >(AI_CHAT_THREAD_DELETE);

  const fetchMoreThreads = useCallback(() => {
    setPage(current => current + 1);
  }, []);

  const refreshThread = useCallback(
    (threadId: string) => {
      getThreadQuery({ variables: { id: threadId } });
    },
    [getThreadQuery],
  );

  const createThread = useCallback(
    async (threadId: string, optimistic?: boolean) => {
      const now = Date.now();

      const thread: AIChatThread = optimistic
        ? {
            id: threadId,
            title: 'New Thread',
            createdDate: now,
            updatedDate: now,
          }
        : await createThreadMutation({
            variables: { input: { threadId } },
          }).then(res => res.data.aiChatThreadCreate);

      setThreads(current => [thread, ...current]);

      setTotal(current => current + 1);

      return thread;
    },
    [createThreadMutation],
  );

  const updateThread = useCallback(async (threadId: string, title: string) => {
    const res = await updateThreadMutation({
      variables: { input: { threadId, title } },
    });

    const thread = res.data.aiChatThreadUpdate;

    setThreads(current =>
      current.map(existing => (existing.id === thread.id ? thread : existing)),
    );

    return thread;
  }, []);

  const deleteThread = useCallback(
    async (threadId: string) => {
      const res = await deleteThreadMutation({ variables: { threadId } });

      setThreads(current => current.filter(({ id }) => id !== threadId));

      setTotal(current => current - 1);

      return res.data.aiChatThreadDelete;
    },
    [deleteThreadMutation],
  );

  const startNewThread = useCallback(() => {
    updateSearchParams({ threadId: uuid(), isNewThread: true });
  }, [updateSearchParams]);

  return useMemo<UseThreadsResponse>(
    () => ({
      loading,
      refreshingThread,
      error,
      currentThread,
      threads,
      hasMoreThreads: threads.length < total,
      fetchMoreThreads,
      refreshThread,
      createThread,
      updateThread,
      deleteThread,
      startNewThread,
    }),
    [
      loading,
      refreshingThread,
      error,
      currentThread,
      threads,
      fetchMoreThreads,
      refreshThread,
      createThread,
      updateThread,
      deleteThread,
      startNewThread,
    ],
  );
};
